import { Dayjs } from 'dayjs';
import { BusinessModuleRef } from '../business-module-ref.js';
import { SerialNumberPool } from './serial-number-pool.js';
import { isNullOrUndefined } from '../../../utility/null-safety.js';
import { DatabaseException } from '../../../exceptions/database-exception.js';
import { EventTicket } from '../event-ticket.js';
import { TicketType, getTicketTypeId, getTicketTypeShortCode } from '../ticket-type.js';
import { SerialNumberScope } from './serial-number-scope.js';
import { LegacySerialNumberStrategy } from './legacy-serial-number-strategy.js';
import { IProductWithSerialNumber } from '../../../contracts/product-with-serial-number.js';
import { ErrorLike } from '../../core/error-like.js';
import { Result } from '../../../utility/result.js';
import { UnsupportedProductType } from './unsupported-product-type.js';
import { SerialNumberStrategy, SerialNumberStrategyUtils } from './serial-number-strategy.js';

/**
 * @deprecated Use {@link StandardSerialNumberPool} instead.
 */
export abstract class LegacyStandardSerialNumberPool extends SerialNumberPool {
  protected serialNumbers: Map<string, number>;
  protected ticketGroups: Map<string, number>;
  protected totalTicketGroups: number;

  constructor(
    public readonly businessRef: BusinessModuleRef,
    public readonly eventId: string | null,
    public readonly creationDate: Dayjs,
    public readonly shortCode: string,
    public readonly scope: SerialNumberScope,
    totalTicketGroups: number,
    ticketGroups: Map<string, number>,
    serialNumbers: Map<string, number>,
  ) {
    super();

    this.serialNumbers = serialNumbers;
    this.ticketGroups = ticketGroups;
    this.totalTicketGroups = totalTicketGroups;
  }

  abstract get strategy(): LegacySerialNumberStrategy;

  getStrategy(): SerialNumberStrategy {
    return SerialNumberStrategyUtils.fromLegacy(this.strategy);
  }

  protected abstract getTicketGroupId(ticket: EventTicket): string | null;
  protected abstract getTicketGroup(ticket: EventTicket): number;

  getSerialNumber(product: IProductWithSerialNumber): Result<string, ErrorLike> {
    if(!(product instanceof EventTicket)) {
      return Result.error(new UnsupportedProductType(product));
    }

    const ticket = product;

    const ticketGroupId: string | null = this.getTicketGroupId(ticket);
    if(isNullOrUndefined(ticketGroupId)) {
      throw new DatabaseException('Cannot assign a serial group for ticket.', 'ticket-not-valid');
    }

    const ticketGroup: number = this.registerTicketGroup(ticketGroupId, ticket);
    const ticketType: number = getTicketTypeId(ticket.ticketType);
    const serialNumber: number = this.incrementSerialNumber(ticketGroupId, ticket.ticketType);

    return Result.ok([
      this.shortCode,
      ticketType.toFixed().padStart(2, '0'),
      ticketGroup.toFixed().padStart(3, '0'),
      serialNumber.toFixed().padStart(4, '0'),
    ].join(SerialNumberPool.DELIMITER));
  }

  private registerTicketGroup(ticketGroupId: string, ticket: EventTicket): number {
    let ticketType: number | null = this.ticketGroups.get(ticketGroupId) ?? null;
    if(isNullOrUndefined(ticketType)) {
      ticketType = this.getTicketGroup(ticket);
      this.ticketGroups.set(ticketGroupId, ticketType);

      ++this.totalTicketGroups;
    }

    return ticketType;
  }

  private incrementSerialNumber(ticketGroupId: string, ticketType: TicketType): number {
    const serialNumberId = `${getTicketTypeShortCode(ticketType)}_${ticketGroupId}`;
    const serialNumber = (this.serialNumbers.get(serialNumberId) ?? 0) + 1;

    this.serialNumbers.set(serialNumberId, serialNumber);
    return serialNumber;
  }
}
